import { useSyncExternalStore } from 'react';
import { NO_FEATURES, RELAY_MAX_FAILURES, planWait, relayCloseIsFailure, silenceThresholdMs } from './liveflow';
import { EULER_CLOSE, type RoomMode, type ChatUser, type SlotInfo, type SlotServerMessage, type StreamEvent, type StreamState } from '../../shared/events';
import { EulerClient } from './euler-client';
import { speakableName } from '../../shared/speech-text';
import { detectLang, filterComment, newFilterState } from './filters';
import { fmt, strings } from './i18n';
import { getSettings, subscribeSettings, type Settings } from './settings';
import { playSfx } from './sfx';
import { SlotClient } from './slot';
import { SpeechQueue, type QueueItem, type SpeechState } from './tts';

export interface FeedRow {
  id: string;
  kind: 'chat' | 'gift' | 'follow' | 'share' | 'join' | 'like';
  user: ChatUser;
  text: string;
  image?: string;
  ts: number;
  /** Joins and likes are coalesced; this is how many were folded in. */
  merged?: number;
}

export interface SessionState {
  username: string | null;
  status: StreamState;
  message: string;
  /** Unix ms when an automatic retry fires, if one is scheduled. */
  retryAt: number | null;
  /** Automatic re-checks used so far while the account shows as not live (see NOT_LIVE_MAX_TRIES). */
  notLiveTries: number;
  slots: SlotInfo | null;
  whitelisted: boolean;
  host: ChatUser | null;
  title: string | null;
  stats: { viewers: number; likes: number; gifts: number; follows: number };
  feed: FeedRow[];
  speech: SpeechState;
  silent: boolean;
}

const MAX_FEED = 300;
/** While an account shows as not live (or restricted), check again every 30 s, up to 10 times. */
export { NOT_LIVE_MAX_TRIES, NOT_LIVE_RETRY_MS } from './liveflow';
const COALESCE_MS = 5000;
/** sharedRoom: close codes a page sees when the room itself dropped (not an answer from Euler). */
const ROOM_CRASH_CODES = new Set([1001, 1006, 1011, 1012]);

const initial = (speech: SpeechState): SessionState => ({
  username: null,
  status: 'idle',
  message: '',
  retryAt: null,
  notLiveTries: 0,
  slots: null,
  whitelisted: false,
  host: null,
  title: null,
  stats: { viewers: 0, likes: 0, gifts: 0, follows: 0 },
  feed: [],
  speech,
  silent: false,
});

class Session {
  readonly tts: SpeechQueue;
  private state: SessionState;
  private listeners = new Set<() => void>();
  private slot: SlotClient | null = null;
  private euler = new EulerClient({
    onOpen: () => this.onEulerOpen(),
    onEvents: (evs) => {
      this.markLive();
      // Room info arrives even before a broadcast starts; only real events prove chat is flowing.
      if (evs.some((e) => e.t !== 'room' && e.t !== 'control')) this.markActivity();
      evs.forEach((e) => this.onEvent(e));
    },
    onClose: (code, reason) => this.onEulerClose(code, reason),
  });
  private filters = newFilterState();
  private retryTimer: number | null = null;
  private startTimer: number | null = null;
  /** Whitelisted streamers: token for the logged-in relay that opens 18+ LIVEs. */
  private relayToken: string | null = null;
  private viaRelay = false;
  private relayFailures = 0;
  /** sharedRoom: token for this streamer's shared upstream (/room); null = connect directly. */
  private roomToken: string | null = null;
  private viaRoom = false;
  /** The current room socket opened (a room that never opens means: fall back to direct). */
  private roomOpened = false;
  /** The room could not be reached once: connect directly until the page restarts. */
  private roomFallback = false;
  /** Next room connection asks the room to restart a silent upstream (silenceWatchdog). */
  private freshNext = false;
  /** sharedRoom: when the page last rejoined a crashed room without showing a reconnect. */
  private quietRejoinAt = 0;
  private retryForever = false;
  /** Which page this is (live, reader, overlay, killfeed), for diagnostics and "Try again". */
  private page = 'live';
  /** Runtime flags from the latest slot grant. */
  private features = NO_FEATURES;
  private diagToken: string | null = null;
  private diagSent = 0;
  private startedAt = 0;
  /** When the current wait for the LIVE began (0 = not waiting). */
  private notLiveSince = 0;
  /** silenceWatchdog: last real event, and re-joins made while silent. */
  private lastActivityAt = 0;
  private silentRejoins = 0;
  private watchdog: number | null = null;
  /** eulerLoginOnDemand: this grant may ask for a logged-in token once, after an anonymous "not live". */
  private loginOnRequest = false;
  private loginTried = false;
  /** Re-taking the slot in the background while chat keeps flowing (see onSlotClose). */
  private quietReslot = false;
  private reslotTimer: number | null = null;
  private backoffMs = 5000;
  private lastJoin: FeedRow | null = null;
  private lastLike: FeedRow | null = null;
  private generation = 0;

  constructor() {
    this.tts = new SpeechQueue(getSettings());
    this.state = initial(this.tts.snapshot);
    this.tts.subscribe((speech) => this.set({ speech }));
    subscribeSettings(() => this.tts.setSettings(getSettings()));
  }

  get(): SessionState {
    return this.state;
  }

  subscribe(l: () => void): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  /**
   * Starts (or restarts) listening to a streamer. `silent` is for OBS overlays (no voice or
   * sounds); `retryForever` keeps re-checking a not-live account every 30 s without the 10-try
   * limit, because an overlay is usually opened well before the LIVE starts.
   */
  start(username: string, opts: { silent?: boolean; retryForever?: boolean; page?: string } = {}): void {
    this.retryForever = Boolean(opts.retryForever);
    this.page = opts.page ?? (opts.silent ? 'overlay' : 'live');
    if (this.state.username === username && this.state.status !== 'idle' && this.state.silent === Boolean(opts.silent)) return;
    this.stop();
    this.generation++;
    this.startedAt = Date.now();
    this.watchdog = window.setInterval(() => this.checkSilence(), 10_000);
    this.filters = newFilterState();
    this.set({ ...initial(this.tts.snapshot), username, silent: Boolean(opts.silent), status: 'connecting', message: '' });
    // Deferred so a mount/unmount/mount cycle (React strict mode, quick navigation) does not
    // open a socket it immediately abandons.
    const gen = this.generation;
    this.startTimer = window.setTimeout(() => {
      this.startTimer = null;
      if (gen === this.generation) this.openSlot();
    }, 50);
  }

  stop(): void {
    this.generation++;
    if (this.watchdog) window.clearInterval(this.watchdog);
    this.watchdog = null;
    this.notLiveSince = 0;
    this.silentRejoins = 0;
    this.diagToken = null;
    this.diagSent = 0;
    if (this.startTimer) window.clearTimeout(this.startTimer);
    this.startTimer = null;
    this.clearRetry();
    this.euler.close();
    this.slot?.close();
    this.slot = null;
    this.tts.clear();
    this.tts.setCloud(null);
    this.relayToken = null;
    this.viaRelay = false;
    this.relayFailures = 0;
    this.roomToken = null;
    this.viaRoom = false;
    this.roomFallback = false;
    this.freshNext = false;
    this.loginOnRequest = false;
    this.loginTried = false;
    this.quietReslot = false;
    if (this.reslotTimer) window.clearTimeout(this.reslotTimer);
    this.reslotTimer = null;
    if (this.state.status !== 'idle') this.set({ status: 'idle', message: '', retryAt: null, username: this.state.username });
  }

  retryNow(): void {
    if (!this.state.username) return;
    this.clearRetry();
    this.backoffMs = 5000;
    const u = this.state.username;
    const opts = { silent: this.state.silent, retryForever: this.retryForever, page: this.page };
    this.stop();
    this.start(u, opts);
  }

  // ---- slot socket ----

  private openSlot(): void {
    const username = this.state.username!;
    const gen = this.generation;
    this.slot = new SlotClient(username, {
      onMessage: (m) => gen === this.generation && this.onSlotMessage(m),
      onClose: (code, reason) => gen === this.generation && this.onSlotClose(code, reason),
    });
    this.slot.connect();
  }

  private onSlotMessage(m: SlotServerMessage): void {
    const t = strings(getSettings().appLang);
    if (this.quietReslot && this.euler.open) {
      if (m.t === 'granted') {
        // Slot is back. The chat connection never stopped, so do not reconnect it; just pick
        // up the fresh tokens for premium voice and the relay.
        this.quietReslot = false;
        this.set({ slots: m.slots, whitelisted: m.whitelisted });
        this.tts.setCloud(m.tts ?? null);
        this.relayToken = m.relay?.token ?? null;
        this.roomToken = m.room?.token ?? null;
        this.loginOnRequest = Boolean(m.loginOnRequest);
        this.features = m.features ?? NO_FEATURES;
        this.diagToken = m.diagToken ?? this.diagToken;
        return;
      }
      if (m.t === 'slots_full' || m.t === 'busy' || m.t === 'error') {
        // Keep listening and try to get the slot back later.
        this.slot = null;
        this.scheduleQuietReslot(30_000);
        return;
      }
    }
    switch (m.t) {
      case 'granted':
        this.set({ slots: m.slots, whitelisted: m.whitelisted, retryAt: null });
        this.tts.setCloud(m.tts ?? null);
        this.relayToken = m.relay?.token ?? null;
        this.loginOnRequest = Boolean(m.loginOnRequest);
        this.loginTried = false;
        this.viaRelay = false;
        // Every re-check cycle starts with a clean relay record (Sept 28: stale failures blocked it).
        this.relayFailures = 0;
        this.features = m.features ?? NO_FEATURES;
        this.diagToken = m.diagToken ?? this.diagToken;
        this.roomToken = m.room?.token ?? null;
        this.diag('granted', `login-on-request=${Boolean(m.loginOnRequest)} relay=${Boolean(m.relay)} room=${this.useRoom}`);
        if (this.useRoom) this.connectRoom('euler');
        else if (m.jwt) this.connectDirect(m.jwt);
        else this.slot?.renew(); // shared grant, but this page falls back to a direct connection
        break;
      case 'jwt':
        this.connectDirect(m.jwt);
        break;
      case 'kicked':
        this.euler.close();
        this.fail('kicked', t.statusKicked, 30_000);
        break;
      case 'slots_full':
        this.set({ slots: m.slots });
        this.fail('slots_full', fmt(t.statusFull, { max: m.slots.max, s: 30 }), 30_000);
        break;
      case 'busy':
        this.fail('busy', fmt(t.statusBusy, { s: m.retryAfter }), Math.max(5, m.retryAfter) * 1000);
        break;
      case 'ip_limit':
        this.fail('error', fmt(t.statusIpLimit, { max: m.max }), null);
        break;
      case 'invalid':
        this.fail('error', t.invalidUsername, null);
        break;
      case 'error':
        this.fail('error', fmt(t.statusError, { message: m.message }), 30_000);
        break;
      case 'hb':
        break;
    }
  }

  private onSlotClose(code: number, _reason: string): void {
    // Rejections already scheduled their own retry; anything else is a dropped connection.
    if (code >= 4001 && code <= 4011) return;
    if (this.state.status === 'idle' || this.state.status === 'ended' || this.state.status === 'offline') return;
    // The slot connection dropped (usually a BacaChat deploy or secret update restarting the
    // slot manager) but chat still flows straight from Euler: keep it, and take the slot back
    // quietly. Listeners hear nothing of it.
    if (this.state.status === 'live' && this.euler.open) {
      this.slot = null;
      this.scheduleQuietReslot(1000 + Math.random() * 4000);
      return;
    }
    this.euler.close();
    this.scheduleReconnect();
  }

  private scheduleQuietReslot(delayMs: number): void {
    this.quietReslot = true;
    if (this.reslotTimer) window.clearTimeout(this.reslotTimer);
    const gen = this.generation;
    this.reslotTimer = window.setTimeout(() => {
      this.reslotTimer = null;
      if (gen !== this.generation || this.slot) return;
      if (!this.euler.open) {
        // Chat stopped meanwhile: fall back to the normal reconnect path.
        this.quietReslot = false;
        this.reopen();
        return;
      }
      this.openSlot();
    }, delayMs);
  }

  // ---- Euler socket ----

  /**
   * Euler accepts the socket before it knows whether the account is live, then closes it with
   * "not live". So an open socket proves nothing; the first real LIVE event does.
   */
  private onEulerOpen(): void {
    this.roomOpened = true;
  }

  private get useRoom(): boolean {
    return Boolean(this.roomToken) && !this.roomFallback;
  }

  private connectDirect(jwt: string): void {
    this.viaRoom = false;
    this.euler.connect(this.state.username!, jwt);
  }

  /** sharedRoom: listen through the streamer's shared upstream; `mode` is the upstream this page needs. */
  private connectRoom(mode: RoomMode): void {
    const user = this.state.username;
    if (!user || !this.roomToken) return;
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const fresh = this.freshNext ? '&fresh=1' : '';
    this.freshNext = false;
    this.viaRoom = true;
    this.roomOpened = false;
    this.euler.connectUrl(`${proto}//${location.host}/room?u=${encodeURIComponent(user)}&t=${encodeURIComponent(this.roomToken)}&mode=${mode}${fresh}`);
  }

  /** How the page is connected, for logs and diagnostics. */
  private via(): string {
    const base = this.viaRelay ? 'relay' : this.loginTried ? 'euler-login' : 'euler';
    return this.viaRoom ? `room-${base}` : base;
  }

  private markLive(): void {
    if (this.state.status === 'live') return;
    if (this.loginTried && !this.viaRelay) console.info('bacachat: the logged-in Euler token opened the LIVE');
    this.diag('connected', `via=${this.via()} waited=${this.notLiveSince ? Date.now() - this.notLiveSince : 0}`);
    this.backoffMs = 5000;
    this.relayFailures = 0;
    this.notLiveSince = 0;
    this.lastActivityAt = Date.now();
    this.set({ status: 'live', message: '', retryAt: null, notLiveTries: 0 });
  }

  private markActivity(): void {
    if (this.silentRejoins > 0 || this.lastActivityAt === 0) this.diag('first-events', `silent-rejoins=${this.silentRejoins}`);
    this.lastActivityAt = Date.now();
    this.silentRejoins = 0;
  }

  /**
   * silenceWatchdog: a page that counts as live but receives no events has usually joined the
   * room before the broadcast started (LIVE Studio creates the room at setup). Such a connection
   * does not start delivering later, so re-join on a gentle backoff until events flow.
   */
  private checkSilence(): void {
    if (!this.features.silenceWatchdog || this.state.status !== 'live' || !this.lastActivityAt) return;
    const silentFor = Date.now() - this.lastActivityAt;
    if (silentFor < silenceThresholdMs(this.silentRejoins)) return;
    this.silentRejoins++;
    this.diag('silent-rejoin', `silent-for=${silentFor} via=${this.via()} n=${this.silentRejoins}`);
    console.info('bacachat: connected but silent, re-joining');
    this.freshNext = true;
    this.euler.close();
    this.viaRelay = false;
    this.lastActivityAt = Date.now();
    this.reopen();
  }

  /** diagnostics flag: report one step (whitelisted pages only). Fire-and-forget. */
  private diag(step: string, detail?: string): void {
    const u = this.state.username;
    if (!this.features.diagnostics || !this.diagToken || !u || this.diagSent >= 300) return;
    this.diagSent++;
    const body = { u, t: this.diagToken, page: this.page, step, ms: Date.now() - this.startedAt, detail, build: typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev' };
    fetch('/api/diag', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body), keepalive: true }).catch(() => {});
  }

  private onEulerClose(code: number, reason: string): void {
    const t = strings(getSettings().appLang);
    const user = this.state.username ?? '';
    this.diag('closed', `code=${code} via=${this.via()} ${reason.slice(0, 80)}`);
    // The shared room could not be reached at all (the socket never opened): fall back to a
    // direct connection for the rest of this page's session, at the same escalation step.
    if (this.viaRoom && !this.roomOpened && this.state.status !== 'idle') {
      console.warn('bacachat: shared room unavailable, connecting directly', code);
      this.diag('room-fallback', `code=${code}`);
      this.roomFallback = true;
      this.viaRoom = false;
      if (this.viaRelay && this.slot?.open) return this.openRelay();
      if (this.slot?.open && this.slot.renew(this.loginTried)) return;
      return this.reopen();
    }
    // Only real relay errors count towards giving up on the relay; "not live" is an answer.
    if (this.viaRelay && relayCloseIsFailure(code)) this.relayFailures++;
    // Euler reports usernames it cannot resolve (typos, deleted accounts, no room at all) as a
    // generic 1011 error. Treat that like "not live" instead of reconnecting in a loop.
    if (code === EULER_CLOSE.INTERNAL_SERVER_ERROR && /could not resolve the user|room-info|not currently live/i.test(reason)) {
      code = EULER_CLOSE.NOT_LIVE;
    }
    // The relay's TikTok login has expired: retrying cannot help until someone renews it.
    if (this.viaRelay && code === EULER_CLOSE.INVALID_AUTH) {
      this.viaRelay = false;
      this.releaseSlot();
      this.fail('restricted', fmt(t.statusLoginExpired, { user: '@' + user }), null);
      return;
    }
    switch (code) {
      case EULER_CLOSE.NOT_LIVE: {
        // Whitelisted streamer and Euler says "not live": if TikTok says the account is live, the
        // LIVE is restricted. Escalate: a logged-in Euler token first (eulerLoginOnDemand), then
        // the logged-in relay.
        const canLogin = this.loginOnRequest && !this.loginTried;
        if (!this.viaRelay && (canLogin || this.relayToken) && this.slot?.open) {
          this.tryEscalate(user);
          return;
        }
        this.viaRelay = false;
        this.releaseSlot();
        this.notLive('offline', fmt(t.statusOffline, { user: '@' + user }));
        // Euler also says "not live" for LIVEs it cannot open (18+ restricted). Ask TikTok directly
        // so the streamer gets the real reason instead of a misleading "not live".
        const gen = this.generation;
        fetch(`/api/live-status?u=${encodeURIComponent(user)}`)
          .then((r) => r.json() as Promise<{ live: boolean | null }>)
          .then((d) => {
            if (d.live !== true || gen !== this.generation || this.state.status !== 'offline') return;
            const tt = strings(getSettings().appLang);
            // Same state, better reason: keep the retry that notLive() already scheduled.
            const left = this.state.retryAt ? this.state.retryAt - Date.now() : 0;
            this.fail('restricted', fmt(this.state.whitelisted ? tt.statusRestrictedLoggedIn : tt.statusRestricted, { user: '@' + user }), left > 0 ? left : null);
          })
          .catch(() => {});
        return;
      }
      case EULER_CLOSE.STREAM_END:
        this.releaseSlot();
        this.ended();
        return;
      case EULER_CLOSE.TOO_MANY_CONNECTIONS:
        console.warn('Euler refused: too many connections');
        this.fail('busy', fmt(t.statusBusy, { s: 60 }), 60_000);
        return;
      case EULER_CLOSE.INVALID_AUTH:
      case EULER_CLOSE.MAX_LIFETIME_EXCEEDED:
      case EULER_CLOSE.TIKTOK_CLOSED_CONNECTION:
      case EULER_CLOSE.INTERNAL_SERVER_ERROR:
      default:
        if (this.state.status === 'idle') return;
        console.info('Euler closed', code, reason);
        // The shared room itself went away mid-LIVE (its Durable Object restarted): rejoin at once,
        // keeping the feed as it is. Once per 30 s; after that the normal backoff applies.
        if (this.viaRoom && this.roomOpened && this.state.status === 'live' && ROOM_CRASH_CODES.has(code) && Date.now() - this.quietRejoinAt > 30_000 && this.slot?.open) {
          this.quietRejoinAt = Date.now();
          this.diag('room-rejoin', `code=${code}`);
          const gen = this.generation;
          this.clearRetry();
          this.retryTimer = window.setTimeout(() => {
            this.retryTimer = null;
            if (gen !== this.generation || this.state.status === 'idle') return;
            this.connectRoom(this.viaRelay ? 'relay' : this.loginTried ? 'login' : 'euler');
          }, 1000);
          return;
        }
        this.scheduleReconnect();
    }
  }

  private scheduleReconnect(): void {
    const t = strings(getSettings().appLang);
    const wait = this.backoffMs;
    this.backoffMs = Math.min(120_000, this.backoffMs * 2);
    this.set({ status: 'connecting', message: fmt(t.statusReconnecting, { s: Math.round(wait / 1000) }), retryAt: Date.now() + wait });
    this.clearRetry();
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null;
      if (this.state.status === 'idle') return;
      if (this.viaRelay && this.slot?.open) {
        this.openRelay();
      } else if (this.slot?.open && this.useRoom) {
        this.connectRoom(this.loginTried ? 'login' : 'euler');
      } else if (this.slot?.open) {
        if (!this.slot.renew()) this.reopen();
      } else {
        this.reopen();
      }
    }, wait);
  }

  private reopen(): void {
    this.quietReslot = false;
    if (this.reslotTimer) window.clearTimeout(this.reslotTimer);
    this.reslotTimer = null;
    this.slot?.close();
    this.slot = null;
    this.set({ status: 'connecting', message: '', retryAt: null });
    this.openSlot();
  }

  /**
   * Asks TikTok whether the account is live; if so the LIVE is restricted, so try the next step:
   * a logged-in Euler token (once per grant, eulerLoginOnDemand), then the relay.
   */
  private tryEscalate(user: string): void {
    const t = strings(getSettings().appLang);
    const gen = this.generation;
    this.set({ status: 'connecting', message: fmt(t.statusOpeningRestricted, { user: '@' + user }), retryAt: null });
    const giveUp = () => {
      if (gen !== this.generation) return;
      this.releaseSlot();
      this.notLive('offline', fmt(t.statusOffline, { user: '@' + user }));
    };
    fetch(`/api/live-status?u=${encodeURIComponent(user)}`)
      .then((r) => r.json() as Promise<{ live: boolean | null }>)
      .then((d) => {
        if (gen !== this.generation) return;
        this.diag('live-status', `live=${d.live}`);
        if (d.live !== true) return giveUp();
        if (this.loginOnRequest && !this.loginTried && this.slot?.open) {
          this.loginTried = true;
          console.info('bacachat: restricted LIVE, asking for a logged-in Euler token');
          this.diag('login-requested', `room=${this.useRoom}`);
          if (this.useRoom) return this.connectRoom('login');
          if (this.slot.renew(true)) return; // the 'jwt' reply reconnects Euler
        }
        if (this.relayToken) return this.openRelay();
        this.releaseSlot();
        this.notLive('restricted', fmt(t.statusRestrictedLoggedIn, { user: '@' + user }));
      })
      .catch(giveUp);
  }

  private openRelay(): void {
    const user = this.state.username;
    if (!user || !this.relayToken) return;
    if (this.relayFailures >= RELAY_MAX_FAILURES) {
      // The relay keeps failing (login expired, TikTok refusing): explain instead of looping.
      const t = strings(getSettings().appLang);
      this.viaRelay = false;
      this.releaseSlot();
      this.notLive('restricted', fmt(t.statusRestrictedLoggedIn, { user: '@' + user }));
      return;
    }
    this.viaRelay = true;
    this.diag('relay', `failures=${this.relayFailures} room=${this.useRoom}`);
    if (this.useRoom) return this.connectRoom('relay');
    this.viaRoom = false;
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    this.euler.connectUrl(`${proto}//${location.host}/relay?u=${encodeURIComponent(user)}&t=${encodeURIComponent(this.relayToken)}`);
  }

  private releaseSlot(): void {
    this.slot?.close();
    this.slot = null;
  }

  /**
   * The account is not live (or the LIVE is restricted): try again every 30 seconds, up to
   * 10 times, so a streamer who opens BacaChat just before going live does not have to tap
   * "Try again". Each try reopens the slot, so a waiting page does not hold one in between.
   */
  private notLive(status: 'offline' | 'restricted', message: string): void {
    if (!this.notLiveSince) this.notLiveSince = Date.now();
    const tries = this.state.notLiveTries;
    const plan = planWait({
      features: this.features,
      whitelisted: this.state.whitelisted,
      retryForever: this.retryForever,
      tries,
      waitedMs: Date.now() - this.notLiveSince,
      status,
    });
    if (plan.kind === 'stop') return this.fail(status, message, null);
    this.set({ notLiveTries: tries + 1 });
    if (plan.kind === 'reopen') return this.fail(status, message, plan.delayMs);
    // fastLiveCheck: ask TikTok's cheap public check; only take a slot and an Euler token once
    // it says live. No slot is held while waiting.
    this.fail(status, message, null);
    this.set({ retryAt: Date.now() + plan.delayMs });
    const gen = this.generation;
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null;
      const user = this.state.username;
      if (gen !== this.generation || !user || this.state.status === 'idle') return;
      fetch(`/api/live-status?u=${encodeURIComponent(user)}`)
        .then((r) => r.json() as Promise<{ live: boolean | null }>)
        .then((d) => {
          if (gen !== this.generation) return;
          if (d.live === true) {
            this.diag('went-live', `waited=${Date.now() - this.notLiveSince}`);
            this.reopen();
          } else this.notLive(status, message);
        })
        .catch(() => gen === this.generation && this.notLive(status, message));
    }, plan.delayMs);
  }

  /** The LIVE ended. Overlays with overlayRejoin keep waiting for the next one. */
  private ended(): void {
    const t = strings(getSettings().appLang);
    this.diag('ended');
    if (this.retryForever && this.features.overlayRejoin) {
      this.notLiveSince = 0;
      this.set({ notLiveTries: 0 });
      return this.fail('ended', t.statusEnded, 60_000);
    }
    this.fail('ended', t.statusEnded, null);
  }

  private fail(status: StreamState, message: string, retryInMs: number | null): void {
    this.clearRetry();
    this.set({ status, message, retryAt: retryInMs ? Date.now() + retryInMs : null });
    if (retryInMs) {
      this.retryTimer = window.setTimeout(() => {
        this.retryTimer = null;
        if (this.state.status === 'idle') return;
        this.reopen();
      }, retryInMs);
    }
  }

  private clearRetry(): void {
    if (this.retryTimer) window.clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  // ---- events ----

  private onEvent(e: StreamEvent): void {
    const s = getSettings();
    const t = strings(s.appLang);
    switch (e.t) {
      case 'room':
        this.set({ host: e.host, title: e.title ?? null });
        return;
      case 'stats':
        if (typeof e.viewers === 'number') this.set({ stats: { ...this.state.stats, viewers: e.viewers } });
        return;
      case 'control':
        if (e.action === 'ended') {
          this.euler.close();
          this.releaseSlot();
          this.ended();
        }
        return;
      case 'chat': {
        const text = filterComment(e.text, s.filters, this.filters, e.ts);
        if (!text) return;
        this.push({ id: e.id, kind: 'chat', user: e.user, text, ts: e.ts });
        this.sfx(s, s.sfx.chat);
        if (s.read.chat) {
          // Emote codes, emoji, slang and names are cleaned at speak time (speech-text.ts),
          // using whichever voice language is active then.
          const speech = s.format.replace('{nickname}', speakableName(e.user.nickname, e.user.uniqueId)).replace('{comment}', text).trim() || text;
          this.say(s, { id: e.id, kind: 'chat', user: e.user, text, speech, lang: this.langFor(s, text) });
        }
        return;
      }
      case 'gift': {
        if (!e.streakEnd) return;
        const text = fmt(t.giftText, { gift: e.giftName, count: e.count });
        this.set({ stats: { ...this.state.stats, gifts: this.state.stats.gifts + 1 } });
        this.push({ id: e.id, kind: 'gift', user: e.user, text, image: e.image, ts: e.ts });
        this.sfx(s, s.sfx.gift);
        if (s.read.gift) this.say(s, { id: e.id, kind: 'gift', user: e.user, text, speech: fmt(t.giftSpeech, { nickname: speakableName(e.user.nickname, e.user.uniqueId), count: e.count, gift: e.giftName }), lang: null });
        return;
      }
      case 'follow':
        this.set({ stats: { ...this.state.stats, follows: this.state.stats.follows + 1 } });
        this.push({ id: e.id, kind: 'follow', user: e.user, text: t.followText, ts: e.ts });
        this.sfx(s, s.sfx.follow);
        if (s.read.follow) this.say(s, { id: e.id, kind: 'follow', user: e.user, text: t.followText, speech: fmt(t.followSpeech, { nickname: speakableName(e.user.nickname, e.user.uniqueId) }), lang: null });
        return;
      case 'share':
        this.push({ id: e.id, kind: 'share', user: e.user, text: t.shareText, ts: e.ts });
        this.sfx(s, s.sfx.share);
        if (s.read.share) this.say(s, { id: e.id, kind: 'share', user: e.user, text: t.shareText, speech: fmt(t.shareSpeech, { nickname: speakableName(e.user.nickname, e.user.uniqueId) }), lang: null });
        return;
      case 'join': {
        const last = this.lastJoin;
        if (last && e.ts - last.ts < COALESCE_MS) {
          const merged = (last.merged ?? 1) + 1;
          this.replace({ ...last, merged, text: fmt(t.joinManyText, { n: merged }), user: merged > 1 ? { uniqueId: '', nickname: `+${merged}` } : last.user });
          return;
        }
        const row: FeedRow = { id: e.id, kind: 'join', user: e.user, text: t.joinText, ts: e.ts, merged: 1 };
        this.lastJoin = row;
        this.push(row);
        if (s.read.join) this.say(s, { id: e.id, kind: 'join', user: e.user, text: t.joinText, speech: fmt(t.joinSpeech, { nickname: speakableName(e.user.nickname, e.user.uniqueId) }), lang: null });
        return;
      }
      case 'like': {
        this.set({ stats: { ...this.state.stats, likes: Math.max(this.state.stats.likes, e.total) } });
        const last = this.lastLike;
        if (last && last.user.uniqueId === e.user.uniqueId && e.ts - last.ts < COALESCE_MS) {
          const merged = (last.merged ?? 0) + e.count;
          this.replace({ ...last, merged, text: fmt(t.likeText, { count: merged }) });
          return;
        }
        const row: FeedRow = { id: e.id, kind: 'like', user: e.user, text: fmt(t.likeText, { count: e.count }), ts: e.ts, merged: e.count };
        this.lastLike = row;
        this.push(row);
        if (s.read.like) this.say(s, { id: e.id, kind: 'like', user: e.user, text: row.text, speech: fmt(t.likeSpeech, { nickname: speakableName(e.user.nickname, e.user.uniqueId), count: e.count }), lang: null });
        return;
      }
    }
  }

  /** A fixed language only when the script demands it; null follows the current voice language. */
  private langFor(s: Settings, text: string): string | null {
    if (!s.detectLanguage) return null;
    const d = detectLang(text);
    if (d === 'zh') return 'zh-CN';
    if (d === 'ta') return 'ta-IN';
    if (d === 'ja') return 'ja-JP';
    if (d === 'ko') return 'ko-KR';
    if (d === 'th') return 'th-TH';
    if (d === 'ar') return 'ar-SA';
    // Latin script: Indonesian, Malay and English cannot be told apart reliably from one short
    // comment, so it follows the voice language the streamer picked.
    return null;
  }

  private say(s: Settings, item: QueueItem): void {
    if (this.state.silent || !s.voiceOn) return;
    this.tts.enqueue(item);
  }

  private sfx(s: Settings, name: Settings['sfx']['gift']): void {
    if (this.state.silent || !s.sfxOn) return;
    playSfx(name, s.volume);
  }

  private push(row: FeedRow): void {
    const feed = [...this.state.feed, row];
    this.set({ feed: feed.length > MAX_FEED ? feed.slice(feed.length - MAX_FEED) : feed });
  }

  private replace(row: FeedRow): void {
    if (row.kind === 'join') this.lastJoin = row;
    if (row.kind === 'like') this.lastLike = row;
    this.set({ feed: this.state.feed.map((r) => (r.id === row.id ? row : r)) });
  }

  private set(patch: Partial<SessionState>): void {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((l) => l());
  }
}

export const session = new Session();

export function useSession(): SessionState {
  return useSyncExternalStore(
    (l) => session.subscribe(l),
    () => session.get(),
    () => session.get(),
  );
}
